import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { TriggerCentralMappingVariant } from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import SettingsCentralMappingDropdownsTriggersTriggerCentralMapping from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import type { CostSettingProfile } from 'lumen/models';
import {
  AUTOMAPPING_STATUS,
  type AutomappingStatus,
} from 'lumen/utils/central-mapping-constants';

type DropdownCostProfileOption = Option & {
  value: CostSettingProfile | null;
};

interface SettingsCentralMappingDropdownsDropdownCostProfileSignature {
  Args: {
    autoCostProfile: CostSettingProfile | null;
    costProfile: CostSettingProfile | null;
    hideAutomappingVisuals?: boolean;
    isMixed?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
    onNavigateToCostSettings?: () => void;
    profiles: CostSettingProfile[];
    setCostProfile: (costProfile: CostSettingProfile | null) => void;
    userCostProfile: CostSettingProfile | null;
    variant: TriggerCentralMappingVariant;
  };
}

export default class SettingsCentralMappingDropdownsDropdownCostProfile extends Component<SettingsCentralMappingDropdownsDropdownCostProfileSignature> {
  @service declare intl: Services['intl'];

  get breadcrumbs(): string[] {
    const { costProfile } = this.args;

    if (!costProfile) {
      return [];
    }

    return [costProfile.name ?? ''];
  }

  get mappingStatus(): AutomappingStatus {
    const { userCostProfile, autoCostProfile, hideAutomappingVisuals } =
      this.args;

    if (userCostProfile) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    if (!hideAutomappingVisuals && autoCostProfile) {
      return AUTOMAPPING_STATUS.AUTO_MAPPED;
    }

    return AUTOMAPPING_STATUS.UNMAPPED;
  }

  // Hidden once profiles exist, to keep the dropdown short.
  get navigateToCostSettingsAction():
    | {
        dataTestId: string;
        iconBefore: IconName;
        label: string;
        onClick: () => void;
      }
    | undefined {
    const { onNavigateToCostSettings, profiles } = this.args;

    if (!onNavigateToCostSettings || profiles.length > 0) {
      return undefined;
    }

    return {
      label: this.intl.t('settings.cost.create'),
      onClick: onNavigateToCostSettings,
      iconBefore: 'plus',
      dataTestId: 'dropdown-cost-profile-create',
    };
  }

  get options(): DropdownCostProfileOption[] {
    const { hideAutomappingVisuals, autoCostProfile, profiles } = this.args;

    const options: DropdownCostProfileOption[] = profiles.map((profile) => {
      const showAutomation =
        !hideAutomappingVisuals && profile === autoCostProfile;

      return {
        value: profile,
        label: profile.name ?? '',
        icon: showAutomation ? 'automation' : undefined,
        unsafe_className: showAutomation
          ? 'mapping-dropdown__option--automation'
          : undefined,
      };
    });

    // Auto-mapped cost profiles can't be unmapped.
    if (isEmpty(autoCostProfile)) {
      options.push({
        value: null,
        label: this.intl.t('general.unmapped'),
      });
    }

    return options;
  }

  get selectedOption(): DropdownCostProfileOption | null {
    return (
      this.options.find((option) => option.value === this.args.costProfile) ??
      null
    );
  }

  @action onChange(option: DropdownCostProfileOption): void {
    this.args.setCostProfile(option.value);
  }

  <template>
    <div data-test-id="dropdown-cost-profile-wrapper">
      <PrismComboSelectInput
        @disabled={{@isReadOnly}}
        @onChange={{this.onChange}}
        @options={{this.options}}
        @unsafe_additionalAction={{this.navigateToCostSettingsAction}}
        @unsafe_triggerComponent={{component
          SettingsCentralMappingDropdownsTriggersTriggerCentralMapping
          breadcrumbs=this.breadcrumbs
          isMixed=@isMixed
          isReadOnly=@isReadOnly
          isSelected=@isSelected
          mappingStatus=this.mappingStatus
          variant=@variant
        }}
        @value={{this.selectedOption}}
        data-test-id="dropdown-cost-profile"
      />
    </div>
  </template>
}
